/**
 * Start values a mission asks for when a run starts (agion/Agion#2413).
 *
 * GET /missions/{id} lists them in `launch_inputs` for a workflow mission:
 * name, type, required (declared required and read by a step), description
 * and an optional default. The chat asks for them in a short form before the
 * run starts and sends what the person entered beside the keys it already
 * sends. A mission without the field, or with an empty list, asks for nothing
 * and the chat starts runs as before.
 */

import type { LaunchInput } from '../types/mission'
import type { UserMessageAttachment } from '@agion/mission-stream'

/** Keys the chat fills itself, except an explicitly declared file_id selection. */
export const CHAT_SUPPLIED_KEYS: ReadonlySet<string> = new Set([
  'message', 'file_id', 'filename', 'content_type', 'attachments', 'previous_execution_id',
])

/** The code orchestration-service puts in `error` when it refuses a launch
 *  that lacks a required start value (HTTP 422); `missing_inputs` lists the
 *  values and `message` says it in a sentence. */
export const START_VALUES_MISSING_CODE = 'MISSION_START_VALUES_MISSING'

/** The default as form text: a string as it is, any other JSON value as JSON. */
function defaultText(value: unknown): string | undefined {
  if (value === undefined || value === null) return undefined
  if (typeof value === 'string') return value
  try {
    return JSON.stringify(value)
  } catch {
    return undefined
  }
}

/**
 * The start values the form asks for, from the raw `launch_inputs`. An entry
 * without a non-empty string name, a repeated name and a key the chat sends
 * itself are left out. A declared file_id file contract needs an explicit
 * upload selection before launch; anything else malformed reads as absent.
 */
export function parseLaunchInputs(raw: unknown): LaunchInput[] {
  if (!Array.isArray(raw)) return []
  const inputs: LaunchInput[] = []
  const seen = new Set<string>()
  for (const entry of raw) {
    if (entry === null || typeof entry !== 'object' || Array.isArray(entry)) continue
    const fields = entry as Record<string, unknown>
    const name = typeof fields.name === 'string' ? fields.name.trim() : ''
    if (name === '' || seen.has(name)) continue
    const input: LaunchInput = {
      name,
      type: typeof fields.type === 'string' && fields.type !== '' ? fields.type : 'string',
      required: fields.required === true,
    }
    if (typeof fields.format === 'string') input.format = fields.format
    if (CHAT_SUPPLIED_KEYS.has(name) && !(name === 'file_id' && isFileLaunchInput(input))) continue
    seen.add(name)
    if (typeof fields.description === 'string' && fields.description.trim() !== '') {
      input.description = fields.description.trim()
    }
    const fallback = defaultText(fields.default)
    if (fallback !== undefined) {
      input.defaultValue = fallback
      input.defaultRaw = fields.default
    }
    inputs.push(input)
  }
  return inputs
}

/** "recipient_email" and "recipientEmail" read as "Recipient email". */
export function humaniseName(name: string): string {
  const words = name
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_\-.]+/g, ' ')
    .trim()
    .toLowerCase()
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : name
}

/**
 * What the form holds when it opens: the value entered for the previous run
 * in this conversation, else the mission's default, else empty.
 */
export function initialLaunchValues(
  inputs: LaunchInput[],
  previous: Record<string, string> | null,
): Record<string, string> {
  const values: Record<string, string> = {}
  for (const input of inputs) {
    const kept = previous?.[input.name]
    values[input.name] = isFileLaunchInput(input) ? '' : typeof kept === 'string' ? kept : input.defaultValue ?? ''
  }
  return values
}

/** Names of required start values that are still empty. */
export function missingRequired(inputs: LaunchInput[], values: Record<string, string>): string[] {
  return inputs.filter((i) => i.required && !(values[i.name] ?? '').trim()).map((i) => i.name)
}

/** Why the form cannot send a value: empty but required, or text that is not of its type. */
export type ValueProblem = 'file' | 'missing' | 'json' | 'number' | 'integer' | 'boolean'

/** Declared types whose text the form sends as parsed JSON. */
const JSON_TYPES: ReadonlySet<string> = new Set(['json', 'object', 'array'])

type Typed = { value: unknown } | { problem: ValueProblem }

/**
 * The value a field's text stands for. A default left as it was goes back as
 * the platform sent it (a number, an object). Otherwise the declared type
 * decides: JSON types parse the text, number, integer and boolean read it,
 * and every other type sends the text as it is.
 */
function typedValue(input: LaunchInput, text: string): Typed {
  if (input.defaultRaw !== undefined && text === input.defaultValue) return { value: input.defaultRaw }
  const type = input.type.trim().toLowerCase()
  const trimmed = text.trim()
  if (JSON_TYPES.has(type)) {
    try {
      return { value: JSON.parse(trimmed) }
    } catch {
      return { problem: 'json' }
    }
  }
  if (type === 'number' || type === 'integer') {
    const n = Number(trimmed)
    if (type === 'number') return Number.isFinite(n) ? { value: n } : { problem: 'number' }
    return Number.isInteger(n) ? { value: n } : { problem: 'integer' }
  }
  if (type === 'boolean') {
    const word = trimmed.toLowerCase()
    return word === 'true' || word === 'false' ? { value: word === 'true' } : { problem: 'boolean' }
  }
  return { value: text }
}

/**
 * The start values the launch sends, or why it cannot: each declared value
 * that is not empty, typed by typedValue. An empty optional value is left
 * out, so the platform fills its default. Any problem stops the launch.
 */
export function prepareLaunchValues(
  inputs: LaunchInput[],
  values: Record<string, string>,
): { values: Record<string, unknown>; problems: Record<string, ValueProblem> } {
  const sent: Record<string, unknown> = {}
  const problems: Record<string, ValueProblem> = {}
  for (const name of missingRequired(inputs, values)) problems[name] = 'missing'
  for (const input of inputs) {
    const text = values[input.name]
    if (typeof text !== 'string' || text.trim() === '') continue
    const typed = typedValue(input, text)
    if ('problem' in typed) problems[input.name] = typed.problem
    else sent[input.name] = typed.value
  }
  return { values: sent, problems }
}

/** The missing start values refusal as the chat reads it. */
export interface StartValuesRefusal {
  /** The values the platform found missing; empty when an older backend does not list them. */
  missingInputs: string[]
}

/**
 * The missing start values refusal, or null for any other error. The body is
 * {"error": code, "message": sentence, "missing_inputs": [names]}; the
 * sentence reaches the person through refusalText as every refusal's does.
 * Only string names are read.
 */
export function readStartValuesRefusal(err: unknown): StartValuesRefusal | null {
  const body = (err as { response?: { data?: unknown } } | null)?.response?.data
  const fields = body !== null && typeof body === 'object' ? (body as Record<string, unknown>) : {}
  if (fields.error !== START_VALUES_MISSING_CODE) return null
  const raw = fields.missing_inputs ?? fields.missingInputs
  const missingInputs = Array.isArray(raw) ? raw.filter((n): n is string => typeof n === 'string' && n !== '') : []
  return { missingInputs }
}

/**
 * The declared start values a launch refusal names, when the error is the
 * missing start values refusal; otherwise null. A refusal that lists none
 * marks none.
 */
export function refusedStartValues(err: unknown, inputs: LaunchInput[]): string[] | null {
  const refusal = readStartValuesRefusal(err)
  if (!refusal) return null
  const missing = new Set(refusal.missingInputs)
  return inputs.filter((i) => missing.has(i.name)).map((i) => i.name)
}

/** Presentation of the server-declared input; no filename or description inference. */
export function isFileLaunchInput(input: LaunchInput): boolean {
  return input.type.trim().toLowerCase() === 'file' || input.format === 'file' || input.format === 'host-file-id'
}

/** Keep explicit file associations; legacy launches use the first attachment. */
export function withLaunchAttachments(
  input: Record<string, unknown>,
  attachments: UserMessageAttachment[],
): Record<string, unknown> {
  const primary = input.file_id === undefined
    ? attachments[0]
    : attachments.find((file) => file.host_file_id === input.file_id)
  return {
    ...input,
    ...(primary ? { file_id: input.file_id ?? primary.host_file_id, filename: primary.filename, content_type: primary.content_type } : {}),
    attachments,
  }
}
